import React, { useEffect, useState } from "react";
import { LoadingDiv, SweetAlertShow } from "../master/Master";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { razorpaySettingUpdate, settingData } from "../../api/Setting";
import SettingLayout from "./SettingLayout";

export default function RazorpaySettings() {
  const [loading, setLoading] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [showWebhook, setShowWebhook] = useState(false);
  const [form, setForm] = useState({
    razorpay_key_id: "",
    razorpay_key_secret: "",
    razorpay_webhook_secret: "",
    razorpay_mode: "test",
  });

  // Fetch from existing setting data (localStorage or API)
  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      try {
        // First try localStorage (already fetched on login)
        const cached = JSON.parse(localStorage.getItem("setting") ?? "{}");
        if (cached?.razorpay_key_id !== undefined) {
          setForm({
            razorpay_key_id: cached.razorpay_key_id ?? "",
            razorpay_key_secret: cached.razorpay_key_secret ?? "",
            razorpay_webhook_secret: cached.razorpay_webhook_secret ?? "",
            razorpay_mode: cached.razorpay_mode ?? "test",
          });
        } else {
          // Fallback: fetch fresh from API
          const response = await settingData();
          if (response.data.status) {
            const s = response.data.data;
            setForm({
              razorpay_key_id: s.razorpay_key_id ?? "",
              razorpay_key_secret: s.razorpay_key_secret ?? "",
              razorpay_webhook_secret: s.razorpay_webhook_secret ?? "",
              razorpay_mode: s.razorpay_mode ?? "test",
            });
          }
        }
      } catch (err) {
        SweetAlertShow(
          "error",
          "Error",
          err?.response?.data?.message ?? err?.message,
        );
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!form.razorpay_key_id.startsWith("rzp_")) {
      return SweetAlertShow(
        "info",
        "Warning",
        "Key ID must start with rzp_test_ or rzp_live_",
      );
    }
    if (!form.razorpay_key_secret || form.razorpay_key_secret.length < 20) {
      return SweetAlertShow(
        "info",
        "Warning",
        "Please enter a valid Key Secret",
      );
    }

    setLoading(true);
    try {
      const response = await razorpaySettingUpdate(form);
      if (response.data.status) {
        // Update localStorage so other pages see updated mode/key_id
        const cached = JSON.parse(localStorage.getItem("setting") ?? "{}");
        localStorage.setItem(
          "setting",
          JSON.stringify({
            ...cached,
            razorpay_key_id: form.razorpay_key_id,
            razorpay_mode: form.razorpay_mode,
            // Don't cache secrets in localStorage
            razorpay_key_secret: undefined,
            razorpay_webhook_secret: undefined,
          }),
        );
        SweetAlertShow("success", "Saved", response.data.message);
      } else {
        SweetAlertShow("info", "Warning", response.data.message);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ?? err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const isLive = form.razorpay_mode === "live";

  return (
    <>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <SettingLayout />
                  <div className="col-12">
                    <div className="card">
                      <div className="card-header d-flex align-items-center justify-content-between py-3">
                        <div>
                          <h5 className="mb-0">
                            <i className="ri-bank-card-line me-2 text-primary" />
                            Razorpay Payment Gateway
                          </h5>
                          <small className="text-muted">
                            Configure your Razorpay API credentials
                          </small>
                        </div>
                        {/* Live / Test toggle */}
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted small">Test</span>
                          <div className="form-check form-switch mb-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id="razorpay_mode"
                              checked={isLive}
                              onChange={(e) =>
                                setForm((f) => ({
                                  ...f,
                                  razorpay_mode: e.target.checked
                                    ? "live"
                                    : "test",
                                }))
                              }
                            />
                          </div>
                          <span
                            className={`small fw-semibold ${isLive ? "text-success" : "text-warning"}`}
                          >
                            Live
                          </span>
                          <span
                            className={`badge ${isLive ? "bg-label-success" : "bg-label-warning"}`}
                          >
                            {isLive ? "Live Mode" : "Test Mode"}
                          </span>
                        </div>
                      </div>

                      <div className="card-body">
                        {/* Mode warning banner */}
                        {isLive ? (
                          <div className="alert alert-success d-flex align-items-center gap-2 py-2 mb-4">
                            <i className="ri-shield-check-line ri-20px" />
                            <span className="small">
                              <strong>Live Mode:</strong> Real payments will be
                              processed. Make sure your keys are from the Live
                              section of Razorpay Dashboard.
                            </span>
                          </div>
                        ) : (
                          <div className="alert alert-warning d-flex align-items-center gap-2 py-2 mb-4">
                            <i className="ri-test-tube-line ri-20px" />
                            <span className="small">
                              <strong>Test Mode:</strong> No real payments. Use
                              Razorpay test cards to simulate transactions.
                            </span>
                          </div>
                        )}

                        {/* Info link */}
                        <div className="alert alert-info py-2 mb-4 small">
                          <i className="ri-information-line me-1" />
                          Get your API keys from{" "}
                          <a
                            href="https://dashboard.razorpay.com/app/keys"
                            target="_blank"
                            rel="noreferrer"
                            className="fw-semibold"
                          >
                            Razorpay Dashboard → Settings → API Keys
                          </a>
                        </div>

                        <form className="row g-4" onSubmit={handleSubmit}>
                          {/* Key ID */}
                          <div className="col-sm-6">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="text"
                                className="form-control"
                                id="razorpay_key_id"
                                placeholder="rzp_test_xxxxxxxxxx"
                                value={form.razorpay_key_id}
                                onChange={(e) =>
                                  setForm((f) => ({
                                    ...f,
                                    razorpay_key_id: e.target.value.trim(),
                                  }))
                                }
                              />
                              <label htmlFor="razorpay_key_id">
                                Key ID <span className="text-danger">*</span>
                              </label>
                            </div>
                            <small className="text-muted ms-1">
                              Starts with <code>rzp_test_</code> or{" "}
                              <code>rzp_live_</code>
                            </small>
                          </div>

                          {/* Key Secret */}
                          <div className="col-sm-6">
                            <div className="form-floating form-floating-outline">
                              <input
                                type={showSecret ? "text" : "password"}
                                className="form-control pe-5"
                                id="razorpay_key_secret"
                                placeholder="Key Secret"
                                value={form.razorpay_key_secret}
                                onChange={(e) =>
                                  setForm((f) => ({
                                    ...f,
                                    razorpay_key_secret: e.target.value.trim(),
                                  }))
                                }
                              />
                              <label htmlFor="razorpay_key_secret">
                                Key Secret{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <button
                                type="button"
                                className="btn btn-link position-absolute end-0 top-50 translate-middle-y pe-3 text-muted"
                                onClick={() => setShowSecret((s) => !s)}
                                tabIndex={-1}
                              >
                                <i
                                  className={`ri-${showSecret ? "eye-off" : "eye"}-line ri-20px`}
                                />
                              </button>
                            </div>
                          </div>

                          {/* Webhook Secret */}
                          <div className="col-sm-12">
                            <div className="form-floating form-floating-outline">
                              <input
                                type={showWebhook ? "text" : "password"}
                                className="form-control pe-5"
                                id="razorpay_webhook_secret"
                                placeholder="Webhook Secret"
                                value={form.razorpay_webhook_secret}
                                onChange={(e) =>
                                  setForm((f) => ({
                                    ...f,
                                    razorpay_webhook_secret:
                                      e.target.value.trim(),
                                  }))
                                }
                              />
                              <label htmlFor="razorpay_webhook_secret">
                                Webhook Secret{" "}
                                <span className="text-muted fw-normal">
                                  (Optional)
                                </span>
                              </label>
                              <button
                                type="button"
                                className="btn btn-link position-absolute end-0 top-50 translate-middle-y pe-3 text-muted"
                                onClick={() => setShowWebhook((s) => !s)}
                                tabIndex={-1}
                              >
                                <i
                                  className={`ri-${showWebhook ? "eye-off" : "eye"}-line ri-20px`}
                                />
                              </button>
                            </div>
                            <small className="text-muted ms-1">
                              From Razorpay Dashboard → Webhooks. Used to verify
                              payment callbacks securely.
                            </small>
                          </div>

                          {/* How each key is used */}
                          <div className="col-sm-12">
                            <div className="table-responsive rounded border">
                              <table className="table table-sm mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th>Field</th>
                                    <th>Used Where</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td>
                                      <code>Key ID</code>
                                    </td>
                                    <td>
                                      Frontend — passed to Razorpay checkout
                                      popup
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>
                                      <code>Key Secret</code>
                                    </td>
                                    <td>
                                      Backend only — order creation & payment
                                      signature verification
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>
                                      <code>Webhook Secret</code>
                                    </td>
                                    <td>
                                      Backend only — verifies Razorpay webhook
                                      callbacks
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>

                          <div className="col-sm-12 text-end">
                            <button type="submit" className="btn btn-primary">
                              <i className="ri-save-line me-2" />
                              Save Razorpay Settings
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
